"use client";
import {
  Calendar,
  User,
  ArrowRightLeft,
  TrendingUp,
  Info,
  Target,
  PlayCircle,
  Award,
  BookOpen,
  ExternalLink,
  Send,
  Download,
  Loader2,
  AlertCircle,
  Briefcase,
  Search,
  Filter,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./RecommendationsPage.css";
import { useState, useEffect, useRef } from "react";
import BackButton from "../shared/backButton";
import Pagination from "../shared/paginator/paginator";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import {
  getMobilityRecommendationsApi,
  shareLearningPathInsidePeople,
  shareBulkLearningPathForRecommendedJob,
  downloadLearningPathForRecommendedJobApi,
} from "../../services/peopleService";
import { getLearningPathApi } from "../../services/upskillingService";
import { useTranslation } from "react-i18next";
import { getDepartmentsListApi } from "../../services/settingsService";
import { currentPositionsApi } from "../../services/peopleService";
import CustomReadinessFilter from "../shared/CustomReadinessFilter";

import { getAllJobRolesApi } from "../../services/jobRolesService";

const ExpandableText = ({
  text,
  lines = 3,
  showMoreLabel = "Show more",
  showLessLabel = "Show less",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = textRef.current;
      if (!el) return;

      const computedStyle = window.getComputedStyle(el);
      const fontSize = parseFloat(computedStyle.fontSize) || 14;
      const lineHeight =
        computedStyle.lineHeight === "normal"
          ? fontSize * 1.4
          : parseFloat(computedStyle.lineHeight) || fontSize * 1.4;
      const collapsedHeight = lineHeight * lines;

      setIsOverflowing(el.scrollHeight > collapsedHeight + 1);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => window.removeEventListener("resize", checkOverflow);
  }, [text, lines]);

  return (
    <div className="rec-description-content">
      <p
        ref={textRef}
        className={`rec-description-value ${
          expanded
            ? "rec-description-value-expanded"
            : "rec-description-value-clamped"
        }`}
        style={{ WebkitLineClamp: lines }}
      >
        {text}
      </p>

      {isOverflowing && (
        <button
          type="button"
          className="rec-description-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
};

const RecommendationsPage = () => {
  const { t } = useTranslation();
  const [recommendations, setRecommendations] = useState([]);
  const [currentRoles, setCurrentRoles] = useState([]);

  const [showLearningPathModal, setShowLearningPathModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [mockLearningPath, setMockLearningPath] = useState([]);
  const [loadingPersonId, setLoadingPersonId] = useState(null);
  const [activeSkillTab, setActiveSkillTab] = useState("soft-skills");
  const [downloading, setDownloading] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterCurrentDept, setFilterCurrentDept] = useState("All");
  const [filterSuggestedDept, setFilterSuggestedDept] = useState("All");
  const [filterCurrentRole, setFilterCurrentRole] = useState("All");
  const [sortBy, setSortBy] = useState("Match Percentage");
  const [filterSuggestedRole, setFilterSuggestedRole] = useState("All");
  const readinessFromUrl = searchParams.getAll("readiness");
  const paramUserIds = searchParams.getAll("userId");
  const [departments, setDepartments] = useState([]);
  const [currentRolesPositions, setCurrentRolesPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const sortOptions = [
    {
      value: "Match Percentage",
      label: t("mobilityPage.filters.matchPercentage"),
    },
    { value: "Name", label: t("common.name") },
    { value: "Current Role", label: t("mobilityPage.current") },
    { value: "Suggested Role", label: t("mobilityPage.suggested") },
  ];
  const READINESS_OPTIONS = ["All", "Mobility-ready", "Mobility-opportunities"];

  const sanitizeSearch = (val) =>
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");
  const removeCountFromUrl = () => {
    if (!searchParams.has("count")) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("count");
    setSearchParams(nextParams, { replace: true });
  };
  const addRecommendation = (r) =>
    setRecommendations((prev) => [
      ...prev,
      { ...r, id: Date.now(), recommendedDate: new Date() },
    ]);
  const downloadExcel = () => {
    // Create CSV content
    const headers = [
      t("recommendationsPage.employeeName"),
      t("recommendationsPage.currentRole"),
      t("recommendationsPage.currentDepartment"),
      t("recommendationsPage.suggestedRole"),
      t("recommendationsPage.suggestedDepartment"),
      t("recommendationsPage.matchPercent"),
      t("recommendationsPage.keySkills"),
      t("recommendationsPage.status"),
      t("recommendationsPage.recommendedDate"),
    ];
    const csvContent = [
      headers.join(","),
      ...recommendations.map((recommendation) =>
        [
          recommendation.employeeName,
          recommendation.currentRole,
          recommendation.currentDept,
          recommendation.suggestedRole,
          recommendation.suggestedDept,
          recommendation.matchPercentage,
          `"${recommendation.keySkills.join(", ")}"`,
          recommendation.status,
          recommendation.recommendedDate.toLocaleDateString(),
        ].join(","),
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t("recommendationsPage.csvFilePrefix")}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  const normalizeList = (arr, fallbackAll = ["All"]) => {
    const cleaned = Array.from(
      new Set((arr || []).map((x) => (x || "").trim()).filter(Boolean)),
    );
    if (!cleaned.length) return fallbackAll;

    const hasReal = cleaned.some((x) => x !== "All");
    if (hasReal) return cleaned.filter((x) => x !== "All");

    return fallbackAll;
  };
  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterCurrentDept && filterCurrentDept !== "All") count++;
    if (filterSuggestedDept && filterSuggestedDept !== "All") count++;
    if (filterCurrentRole && filterCurrentRole !== "All") count++;
    if (filterSuggestedRole && filterSuggestedRole !== "All") count++;
    if (
      filterMobilityStatus &&
      filterMobilityStatus.length > 0 &&
      !filterMobilityStatus.includes("All")
    )
      count++;
    if (sortBy && sortBy !== "Match Percentage") count++;
    return count;
  };

  const getInitialMobilityStatus = () => {
    // 1) if readiness is provided in URL, use it
    if (readinessFromUrl.length)
      return normalizeList(readinessFromUrl, ["All"]);

    // 2) keep your old behavior for userId deep links (optional)
    if (paramUserIds.length)
      return ["Mobility-ready", "Mobility-opportunities"];

    // 3) default = All
    return ["All"];
  };
  const [filterMobilityStatus, setFilterMobilityStatus] = useState(
    getInitialMobilityStatus,
  );

  useEffect(() => {
    setFilterMobilityStatus(getInitialMobilityStatus());
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const getMatchColor = (percentage) => {
    if (percentage >= 90) return "match-excellent";
    if (percentage >= 80) return "match-good";
    return "match-average";
  };

  // useEffect(() => {
  //   getMobilityRecommendationsApiHandler();
  // }, [currentPage, itemsPerPage]);

  const getCurrentRolePositions = async () => {
    try {
      const response = await currentPositionsApi();

      //  ADD ALL OPTION IN THE SETCURRENTROLESPOSITIONS DROPDOWN TO SHOW ALL ROLES IN INTIAL LOAD THE API RESPONSE IS ARRAY OF STRINGS

      setCurrentRolesPositions([
        { roleName: t("common.all") },
        ...response.data.map((role) => ({ roleName: role })),
      ]);
    } catch (error) {
      console.error("Error fetching current positions:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  // API call to get all job roles
  const handleRolesDataListApi = async () => {
    try {
      const response = await getAllJobRolesApi();
      // console.log("Job Roles fetched:", response);
      // setCurrentRoles(response.data);
      setCurrentRoles([
        { roleName: t("common.all"), _id: "All" },
        ...response.data,
      ]);
      console.log("Job Roles fetched:", currentRoles);

      // handle response if needed
    } catch (error) {
      console.error("Error fetching job roles:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (selectedList, selectedItem) => {
    removeCountFromUrl();

    if (selectedItem === "All") {
      setFilterMobilityStatus(["All"]);
      return;
    }

    const keys = selectedList.filter((k) => k !== "All");
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
  };
  const handleRemove = (selectedList, removedItem) => {
    removeCountFromUrl();

    const keys = selectedList.filter((k) => k !== "All");
    setFilterMobilityStatus(keys.length ? keys : ["All"]);
  };
  const mobilityReadinessTooltip = (option) => {
    if (option === "Mobility-ready")
      return t("mobilityPage.tooltips.filterTooltip.mobilityReady");
    if (option === "Mobility-opportunities")
      return t("mobilityPage.tooltips.filterTooltip.mobilityOpportunities");
    return "";
  };

  useEffect(() => {
    getMobilityRecommendationsApiHandler();
  }, [
    currentPage,
    searchTerm,
    itemsPerPage,
    filterCurrentDept,
    filterSuggestedDept,
    filterMobilityStatus,
    filterCurrentRole,
    filterSuggestedRole,
    sortBy,
    // activeMobilityTab,
  ]);

  const getMobilityRecommendationsApiHandler = async () => {
    try {
      setLoading(true);
      const obj = {
        pageSize: itemsPerPage,
        pageNo: currentPage,
        search: searchTerm,
        currentDepartment: filterCurrentDept,
        suggestedDepartment: filterSuggestedDept,
        readiness: filterMobilityStatus,
        currentPosition: filterCurrentRole,
        suggestedPosition: filterSuggestedRole,
        sortBy: sortBy,
      };
      const response = await getMobilityRecommendationsApi(obj);
      setRecommendations(response.data);
      setTotalCount(response.totalCount || response.data.length);
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setLoading(false);
    }
  };
  // GET DEPARTMENTS DROPDOWN DATA
  const getDepartmentsDropDownApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      // ADD "ALL" OPTION IN THE SETDEPARTMENTS DROPDOWN TO SHOW ALL DEPARTMENTS IN INTIAL LOAD
      setDepartments([{ name: t("common.all"), _id: "All" }, ...response.data]);
      console.log("Departments fetched:", departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  useEffect(() => {
    getDepartmentsDropDownApiHandler();
    handleRolesDataListApi();
    getCurrentRolePositions();
  }, []);
  const getLearningPathApiHandler = async (person) => {
    setSelectedPerson(person);
    setLoadingPersonId(person._id);
    try {
      // Check if learning path exists in the person object from API response
      if (person.learningPath && person.learningPath.length > 0) {
        setShowLearningPathModal(true);
        document.body.classList.add("mobility-learning-path-modal-open");
        setMockLearningPath(person.learningPath);
        console.log(
          "Learning path loaded from recommendation:",
          person.learningPath,
        );
      } else {
        // Fallback to API call if learning path not in response
        const response = await getLearningPathApi({
          userId: person._id,
        });
        setShowLearningPathModal(true);
        document.body.classList.add("mobility-learning-path-modal-open");
        setMockLearningPath(response.data.learningPath || []);
        console.log("Learning path response:", response);
      }
    } catch (error) {
      setLoadingPersonId(null);
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setLoadingPersonId(null);
    }
  };

  const shareLearningPathHandler = async () => {
    if (!selectedPerson) return;
    setLoadingPersonId(selectedPerson._id);
    try {
      // await shareLearningPathInsidePeople({ userIdArray: [selectedPerson._id] })
      await shareBulkLearningPathForRecommendedJob({
        recommendationIdArray: [selectedPerson._id],
      });
      console.log("Learning path shared successfully");
    } catch (error) {
      handleApiError(error, { showModal: false });
    } finally {
      setLoadingPersonId(null);
    }
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleDownloadPDF = async () => {
    if (!selectedPerson) return;
    setDownloading(true);

    try {
      const res = await downloadLearningPathForRecommendedJobApi({
        recommendationId: selectedPerson._id,
      });

      const contentType = res.headers?.["content-type"] ?? "application/pdf";
      const blob = new Blob([res.data], { type: contentType });

      const cd = res.headers?.["content-disposition"];
      const filename =
        getFileNameFromContentDisposition(cd) ||
        `${(
          selectedPerson.name ||
          t("recommendationsPage.learningPathFileFallback")
        ).replace(/\s+/g, "_")}_${t(
          "recommendationsPage.learningPathPdfSuffix",
        )}.pdf`;

      downloadBlob(blob, filename);
    } catch (error) {
      handleApiError(error, { showModal: false });
    } finally {
      setDownloading(false);
    }
  };

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  function getFileNameFromContentDisposition(cd) {
    if (!cd) return undefined;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
    const raw = m?.[1] || m?.[2];
    return raw ? decodeURIComponent(raw) : undefined;
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const getLevelColor = (level) => {
    const lowerLevel = (level || "").toLowerCase();
    if (lowerLevel.includes("beginner"))
      return "mobility-course-level-beginner";
    if (lowerLevel.includes("intermediate"))
      return "mobility-course-level-intermediate";
    if (lowerLevel.includes("advanced"))
      return "mobility-course-level-advanced";
    return "mobility-course-level-default";
  };

  const renderStarRating = (rating = 0) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="mobility-star-filled">
            ★
          </span>,
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <span key={i} className="mobility-star-half">
            ★
          </span>,
        );
      } else {
        stars.push(
          <span key={i} className="mobility-star-empty">
            ☆
          </span>,
        );
      }
    }

    return (
      <div className="mobility-course-rating">
        <div className="mobility-course-rating-stars">{stars}</div>
        <span className="mobility-rating-number">({rating.toFixed(1)})</span>
      </div>
    );
  };

  const jobSkills = (mockLearningPath || []).filter(
    (s) => s.type === "Job Skill",
  );
  const softSkills = (mockLearningPath || []).filter(
    (s) => s.type === "Soft Skill",
  );
  const filteredSkills =
    activeSkillTab === "job-skills" ? jobSkills : softSkills;
  const noSkillsInBothTabs = jobSkills.length === 0 && softSkills.length === 0;
  const allGoodCopy = t("upskillPage.learningPathModal.allGood");

  const RecommendationSkeletonCard = () => (
    <div className="recommendation-card recommendation-card-skeleton">
      <div className="rc-skeleton-header">
        <div className="rc-skeleton-avatar rc-skeleton-shimmer" />
        <div className="rc-skeleton-text-group">
          <div className="rc-skeleton-line rc-skeleton-line-lg rc-skeleton-shimmer" />
        </div>
      </div>

      <div className="rc-skeleton-roles">
        <div className="rc-skeleton-role-block rc-skeleton-shimmer" />
      </div>

      <div className="rc-skeleton-text-group">
        <div className="rc-skeleton-line rc-skeleton-line-md rc-skeleton-shimmer" />
        <div className="rc-skeleton-line rc-skeleton-line-sm rc-skeleton-shimmer" />
      </div>

      <div className="rc-skeleton-footer">
        <div className="rc-skeleton-line rc-skeleton-line-sm rc-skeleton-shimmer" />
      </div>
    </div>
  );

  return (
    <div className="recommendations-page">
      <BackButton />
      <main className="recommendations-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <ArrowRightLeft size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("recommendationsPage.pageTitle")}</h1>
              <span className="rec-title-info-wrap">
                <button
                  type="button"
                  className="rec-title-info-btn"
                  aria-label={t("recommendationsPage.tooltips.matchTitle")}
                >
                  <Info size={18} />
                </button>

                <span className="rec-title-info-popover" role="tooltip">
                  <strong>{t("recommendationsPage.tooltips.matchTitle")}</strong>
                  <span>{t("recommendationsPage.tooltips.matchMessage")}</span>
                </span>
              </span>
            </div>

            <p>{t("recommendationsPage.pageDescription")}</p>
          </div>

          {/* <div className="aactions-bar">
            <div className="astats">
              <span className="astat-item">
                {t("recommendationsPage.totalRecommendations")}:{" "}
                <strong>{loading ? "…" : totalCount}</strong>
              </span>
            </div>
      
          </div> */}

          {/* Smart Search & Filter panel (same as Employees) */}
          <div className="smart-filter-panel-team">
            <div className="smart-filter-top-bar">
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("mobilityPage.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) => {
                      setSearchInputValue(sanitizeSearch(e.target.value));
                    }}
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("mobilityPage.searchPlaceholder")}
                  </span>
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={handleSearchButtonClick}
                  >
                    <Search size={18} />
                  </button>
                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              {/* Icons pinned to the right */}
              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${
                      showSearchBar ? "active" : ""
                    }`}
                    onClick={() => {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${
                    showFiltersPanel ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>
              <div className="smart-filter-left">
                <div className="smart-filter-info">
                  <div className="div-container">
                    {getAppliedFiltersCount() > 0 && (
                      <>
                        <span className="smart-filter-badge">
                          {getAppliedFiltersCount()}
                        </span>
                        <span> {t("common.filtersApplied")}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>
                        {t("mobilityPage.filters.currentDepartment")}
                      </label>
                      <select
                        value={filterCurrentDept}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/settings/organization-info");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterCurrentDept(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterCurrentDept !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noDepartmentsAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createDepartment")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectDepartment")}
                            </option>
                            {departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>
                        {t("mobilityPage.filters.suggestedDepartment")}
                      </label>
                      <select
                        value={filterSuggestedDept}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/settings/organization-info");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterSuggestedDept(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterSuggestedDept !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noDepartmentsAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createDepartment")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectDepartment")}
                            </option>
                            {departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.currentRole")}</label>
                      <select
                        value={filterCurrentRole}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("job-roles");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterCurrentRole(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterCurrentRole !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {currentRolesPositions.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noRolesAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createRole")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectRole")}
                            </option>
                            {currentRolesPositions.map((role) => (
                              <option key={role._id} value={role.roleName}>
                                {role.roleName}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.suggestedRole")}</label>
                      <select
                        value={filterSuggestedRole}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigate("/job-roles");
                            return;
                          }
                          removeCountFromUrl();
                          setFilterSuggestedRole(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterSuggestedRole !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {currentRoles.length === 0 ? (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.noRolesAvailable")}
                            </option>
                            <option value="__add__">
                              {t("mobilityPage.filters.createRole")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">
                              {t("mobilityPage.filters.selectRole")}
                            </option>
                            {currentRoles.map((role) => (
                              <option key={role._id} value={role.roleName}>
                                {role.roleName}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.sortBy")}</label>
                      <select
                        value={sortBy}
                        onChange={(e) => {
                          removeCountFromUrl();
                          setSortBy(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          sortBy !== "Match Percentage" ? "smart-active" : ""
                        }`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("mobilityPage.filters.readiness")}</label>
                      <CustomReadinessFilter
                        options={READINESS_OPTIONS}
                        selectedValues={filterMobilityStatus}
                        onSelect={handleSelect}
                        onRemove={handleRemove}
                        label={t("mobilityPage.filters.readiness")}
                        getTooltip={mobilityReadinessTooltip}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {loading ? (
            // 🔹 SKELETON VIEW WHILE FETCHING
            <div className="recommendations-grid">
              {Array.from({ length: 6 }).map((_, idx) => (
                <RecommendationSkeletonCard key={idx} />
              ))}
            </div>
          ) : recommendations.length === 0 ? (
            <div className="empty-state">
              <ArrowRightLeft size={48} />
              <h3>{t("recommendationsPage.noMobilityRecommendations")}</h3>
              <p>{t("recommendationsPage.internalMobility")}</p>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <button
                  className="btn btn-primary"
                  onClick={() => onNavigate("/talent-development/mobility")}
                >
                  {t("recommendationsPage.goToInternalMobility")}
                </button>
              </div>
            </div>
          ) : (
            <div className="recommendations-grid">
              {recommendations.map((recommendation) => (
                <div key={recommendation.id} className="recommendation-card">
                  <div className="card-header">
                    <div className="person-info">
                      <div className="person-avatar">
                        <User size={20} />
                      </div>
                      <div>
                        <h3>{recommendation.name}</h3>
                      </div>
                    </div>
                    <div
                      className={`match-score ${getMatchColor(recommendation.totalMatchPercentage)}`}
                    >
                      <TrendingUp size={14} />
                      {recommendation.totalMatchPercentage}%{" "}
                      {t("recommendationsPage.match")}
                    </div>
                  </div>

                  <div className="role-transition">
                    <div className="current-role">
                      <h4>{t("recommendationsPage.currentPosition")}</h4>
                      <p className="role-title">
                        {recommendation.currentPosition}
                      </p>
                      <p className="dept-name">
                        {recommendation.currentDepartment}
                      </p>
                    </div>

                    <div className="arrow">
                      <ArrowRightLeft size={20} />
                    </div>

                    <div className="suggested-role">
                      <h4>{t("recommendationsPage.recommendedPosition")}</h4>
                      <p className="role-title">
                        {recommendation.suggestedPosition}
                      </p>
                      <p className="dept-name">
                        {recommendation.suggestedDepartment}
                      </p>
                    </div>
                  </div>

                  <div className="rec-reason-description">
                    <div className="rec-reason-item">
                      <h4 className="rec-reason-label">
                        {" "}
                        {t("recommendationsPage.reason")}
                      </h4>
                      <p className="rec-reason-value">
                        {recommendation.reason || t("common.notSpecified")}
                      </p>
                    </div>
                    <div className="rec-description-item">
                      <h4 className="rec-description-label">
                        {t("recommendationsPage.description")}
                      </h4>
                      <ExpandableText
                        text={
                          recommendation.comments ||
                          t("recommendationsPage.noDescriptionAvailable")
                        }
                        lines={3}
                        showMoreLabel={t("common.showMore")}
                        showLessLabel={t("common.showLess")}
                      />
                    </div>
                  </div>

                  <div className="recommendation-details">
                    <div className="detail-item">
                      <Calendar size={16} />
                      <span>
                        {t("recommendationsPage.recommended")}:{" "}
                        {new Date(
                          recommendation?.updatedAt,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                    {/*<div className="detail-item">
                      <span className={`status-badge status-${recommendation.status.toLowerCase()}`}>
                        {recommendation.status}
                      </span>
                    </div>*/}
                  </div>

                  <button
                    className="btn btn-primary mobility-assign-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      getLearningPathApiHandler(recommendation);
                    }}
                    disabled={loadingPersonId === recommendation._id}
                  >
                    {loadingPersonId === recommendation._id ? (
                      <>
                        <div className="mobility-btn-spinner"></div>
                        {t("common.loading")}
                      </>
                    ) : (
                      <>{t("recommendationsPage.viewLearningPath")}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {totalCount > 0 && (
            <Pagination
              loading={loading}
              total={totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={recommendations.length}
              onPageChange={setCurrentPage}
              pageSizeOptions={[10, 20, 40, 100]}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((totalCount || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("mobilityPage.paginationInfo", { start, end, total })
              }
            />
          )}
        </div>
      </main>

      {showLearningPathModal && selectedPerson && (
        <div className="mobility-learning-path-modal-overlay">
          <div className="mobility-learning-path-modal">
            <div className="mobility-learning-path-modal-header">
              <div className="mobility-modal-header-content">
                <div className="mobility-modal-title-section">
                  <div className="mobility-upskill-modal-icon">
                    <Target size={24} />
                  </div>
                  <div>
                    <h2>{t("upskillPage.learningPathModal.title")}</h2>
                    <p>
                      {t("upskillPage.learningPathModal.description", {
                        name: selectedPerson.name,
                      })}
                    </p>
                  </div>
                </div>
                <div className="mobility-modal-actions">
                  <button
                    className="mobility-download-btn"
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    aria-busy={downloading}
                    aria-live="polite"
                  >
                    {downloading ? (
                      <>
                        <span
                          className="mobility-btn-spinner"
                          aria-hidden="true"
                        />
                        {t("upskillPage.learningPathModal.downloading")}
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        {t(
                          "upskillPage.learningPathModal.downloadLearningPath",
                        )}
                      </>
                    )}
                  </button>

                  <button
                    onClick={shareLearningPathHandler}
                    className="mobility-download-btn"
                    title={t("upskillPage.learningPathModal.sharePdfTitle")}
                    disabled={loadingPersonId === selectedPerson._id}
                  >
                    {loadingPersonId === selectedPerson._id ? (
                      <Loader2
                        size={18}
                        className="mobility-spin"
                        aria-hidden
                      />
                    ) : (
                      <Send size={18} />
                    )}
                    <span style={{ marginLeft: 8 }}>
                      {t("upskillPage.learningPathModal.sharePdf")}{" "}
                      {selectedPerson.name}
                    </span>
                  </button>
                  <button
                    className="mobility-close-btn"
                    onClick={() => {
                      setShowLearningPathModal(false);
                      setMockLearningPath([]);
                      document.body.classList.remove(
                        "mobility-learning-path-modal-open",
                      );
                    }}
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>

            <div className="mobility-learning-path-content">
              <div className="mobility-upskill-tabs-container">
                <div className="mobility-upskill-tabs-header">
                  <button
                    className={`mobility-upskill-tab-button ${
                      activeSkillTab === "job-skills"
                        ? "mobility-upskill-tab-active"
                        : ""
                    }`}
                    onClick={() => setActiveSkillTab("job-skills")}
                  >
                    <Briefcase size={16} />
                    {t("upskillPage.jobSkills")}
                  </button>
                  <button
                    className={`mobility-upskill-tab-button ${
                      activeSkillTab === "soft-skills"
                        ? "mobility-upskill-tab-active"
                        : ""
                    }`}
                    onClick={() => setActiveSkillTab("soft-skills")}
                  >
                    <User size={16} />
                    {t("upskillPage.softSkills")}
                  </button>
                </div>
              </div>

              <div className="mobility-skills-recommendations">
                {noSkillsInBothTabs ? (
                  <div
                    className="mobility-no-recs-message"
                    role="status"
                    aria-live="polite"
                  >
                    {allGoodCopy}
                  </div>
                ) : filteredSkills.length ? (
                  filteredSkills.map((skill, index) => (
                    <div
                      key={index}
                      className="mobility-skill-recommendation-card"
                    >
                      <div className="mobility-skill-header">
                        <div className="mobility-skill-info">
                          <h3>{skill.skill_gap}</h3>
                        </div>
                      </div>

                      {skill.recommended_courses &&
                        skill.recommended_courses.length > 0 && (
                          <div className="mobility-courses-section">
                            <h4>
                              <BookOpen size={16} />
                              {t("upskillPage.recommendedCourses")}
                            </h4>
                            <div className="mobility-courses-grid-improved">
                              {skill.recommended_courses.map(
                                (course, courseIndex) => (
                                  <div
                                    key={courseIndex}
                                    className="mobility-course-card-improved"
                                  >
                                    <div className="mobility-course-card-header">
                                      <div className="mobility-course-icon">
                                        <PlayCircle size={20} />
                                      </div>
                                      <div className="mobility-course-level-badge">
                                        <Award size={12} />
                                        <span
                                          className={`mobility-level-text ${getLevelColor(course.level)}`}
                                        >
                                          {course.level ||
                                            t(
                                              "upskillPage.learningPathModal.beginner",
                                            )}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="mobility-course-content">
                                      <h5 className="mobility-course-title">
                                        {course.course_name}
                                      </h5>

                                      {renderStarRating(course.rating)}
                                    </div>

                                    <div className="mobility-course-footer">
                                      <span className="mobility-course-platform-tag">
                                        {course.platform ||
                                          t(
                                            "upskillPage.trainingModal.platform",
                                          )}
                                      </span>
                                      <a
                                        href={course.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary mobility-course-link-btn"
                                      >
                                        <ExternalLink size={14} />
                                        {t("common.view")}
                                      </a>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {skill.steps && skill.steps.length > 0 && (
                        <div className="mobility-steps-section">
                          <h4>
                            <Target size={16} />
                            {t("upskillPage.learningPathModal.learningJourney")}
                          </h4>
                          <div className="mobility-steps-timeline">
                            {skill.steps.map((step, stepIndex) => (
                              <div
                                key={stepIndex}
                                className="mobility-step-item"
                              >
                                <div className="mobility-step-indicator">
                                  <div className="mobility-step-number">
                                    {stepIndex + 1}
                                  </div>
                                  <div className="mobility-step-stage">
                                    {step.stage}
                                  </div>
                                </div>
                                <div className="mobility-step-content">
                                  <p>{step.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {skill.note && (
                        <div className="mobility-note-section">
                          <div className="mobility-note-header">
                            <AlertCircle size={16} />
                            <span>
                              {t("upskillPage.learningPathModal.importantNote")}
                            </span>
                          </div>
                          <p>{skill.note}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div
                    className="mobility-no-recs-message"
                    role="status"
                    aria-live="polite"
                  >
                    {activeSkillTab === "job-skills"
                      ? t(
                          "upskillPage.learningPathModal.noJobSkillsNeedUpskilling",
                        )
                      : t(
                          "upskillPage.learningPathModal.noSoftSkillsNeedUpskilling",
                        )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default RecommendationsPage;
